import React, { useEffect, useState, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PlansView from './components/PlansView';
import PlanNavigationBoundary from './components/PlanNavigationBoundary';
import CanvasView from './components/CanvasView';
import AuthPage from './components/AuthPage';
import WelcomePage from './components/WelcomePage';
import { applyTheme, getSavedTheme, saveTheme, resolveTheme, getSystemTheme } from './theme';
import './index.css';

const API = process.env.REACT_APP_API_URL || '';
export { API };

// ── Auth context ──────────────────────────────────────────────────────────────
export const AuthContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }

function getStoredAuth() {
  try {
    const token = localStorage.getItem('ic_token') || sessionStorage.getItem('ic_token');
    const user  = localStorage.getItem('ic_user')  || sessionStorage.getItem('ic_user');
    if (token && user) return { token, user: JSON.parse(user) };
  } catch {}
  return null;
}

// ── Theme provider wrapper ────────────────────────────────────────────────────
function AppShell({ auth, onAuth, onLogout }) {
  const [themePref, setThemePref] = useState(getSavedTheme());

  useEffect(() => {
    applyTheme(resolveTheme(themePref));
    if (themePref === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => applyTheme(getSystemTheme());
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
  }, [themePref]);

  function setTheme(pref) { setThemePref(pref); saveTheme(pref); }

  return (
    <AuthContext.Provider value={{ user: auth?.user || null, token: auth?.token || null, onLogout }}>
      <PlanNavigationBoundary>{routeLocation => <Routes location={routeLocation}>
        {/* Auth-required routes */}
        <Route path="/" element={auth ? <PlansRoute themePref={themePref} onThemeChange={setTheme} /> : <AuthPage onAuth={onAuth} />} />
        {/* Account setup — post-activation set-password */}
        <Route path="/welcome" element={<WelcomePage mode="welcome" onAuth={onAuth} />} />
        {/* Password reset */}
        <Route path="/reset-password" element={<WelcomePage mode="reset" onAuth={onAuth} />} />
        {/* Plan routes — publicly accessible in read-only mode, owner gets edit */}
        <Route path="/p/:planId" element={<CanvasRoute themePref={themePref} onThemeChange={setTheme} />} />
        <Route path="/p/:planId/b/:boxId" element={<CanvasRoute themePref={themePref} onThemeChange={setTheme} />} />
        <Route path="/p/:planId/focus/:boxId" element={<CanvasRoute themePref={themePref} onThemeChange={setTheme} />} />
        {/* Share routes — scoped to a box subtree via share token */}
        <Route path="/s/:shareToken" element={<ShareRoute themePref={themePref} onThemeChange={setTheme} />} />
        {/* Public summary share page */}
        <Route path="/summary/:token" element={<SummarySharePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>}</PlanNavigationBoundary>
    </AuthContext.Provider>
  );
}

// ── Plans route ───────────────────────────────────────────────────────────────
function PlansRoute({ themePref, onThemeChange }) {
  const navigate = useNavigate();
  const { token } = useAuth();
  return (
    <motion.div key="plans" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }} style={{ width: '100%', height: '100vh' }}>
      <PlansView
        token={token}
        onOpenPlan={plan => navigate(`/p/${plan.id}`)}
        themePref={themePref}
        onThemeChange={onThemeChange}
      />
    </motion.div>
  );
}

// ── Canvas route ──────────────────────────────────────────────────────────────
function CanvasRoute({ themePref, onThemeChange, readOnly = false }) {
  const { planId, boxId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth?.token || null;
  const currentUserId = auth?.user?.id || null;

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If we already have the correct plan loaded, skip re-fetch (prevents remount on back/focus URL changes)
    if (plan && plan.id === planId) return;
    // Try authed endpoint first, fall back to public
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    // For share routes or anonymous, use public endpoint
    fetch(`${API}/api/plans/${planId}/public`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(found => {
        if (!found) {
          if (!readOnly && token) {
            // Try authed plans list as fallback
            fetch(`${API}/api/plans`, { headers: { Authorization: `Bearer ${token}` } })
              .then(r => r.json())
              .then(plans => {
                const p = plans.find(p => p.id === planId);
                if (!p) { navigate('/', { replace: true }); return; }
                setPlan(p);
                setLoading(false);
              });
          } else {
            navigate('/', { replace: true });
          }
          return;
        }
        setPlan(found);
        setLoading(false);
      })
      .catch(() => navigate('/', { replace: true }));
  }, [planId, token]);

  if (loading) return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ repeat: Infinity, duration: 1.2 }}
        style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</motion.div>
    </div>
  );

  // Canvas's isOwner flag means edit access; accepted collaborators are editors too.
  const isOwner = !readOnly && currentUserId && ['owner', 'editor'].includes(plan.role);

  return (
    <motion.div key="canvas" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }} style={{ width: '100%', height: '100vh' }}>
      <CanvasView
        plan={plan}
        onPlanUpdated={updated => setPlan(current => ({ ...current, ...updated }))}
        token={token}
        isOwner={isOwner}
        initialBoxId={boxId || null}
        onBack={() => navigate('/')}
        themePref={themePref}
        onThemeChange={onThemeChange}
      />
    </motion.div>
  );
}


// ── Share route ────────────────────────────────────────────────────────────────
function ShareRoute({ themePref, onThemeChange }) {
  const { shareToken } = useParams();
  const [plan, setPlan] = useState(null);
  const [shareRoot, setShareRoot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/shares/${shareToken}`)
      .then(r => r.ok ? r.json() : Promise.reject('Invalid share link'))
      .then(({ planId, rootBoxId }) => {
        setShareRoot(rootBoxId);
        return fetch(`${API}/api/plans/${planId}/public`)
          .then(r => r.ok ? r.json() : Promise.reject('Plan not found'))
          .then(p => { setPlan(p); setLoading(false); });
      })
      .catch(err => { setError(typeof err === 'string' ? err : 'Invalid share link'); setLoading(false); });
  }, [shareToken]);

  if (loading) return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ repeat: Infinity, duration: 1.2 }}
        style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</motion.div>
    </div>
  );

  if (error) return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 16, color: 'var(--text-primary)', fontWeight: 600 }}>{error}</div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>This share link may have expired or been removed.</div>
    </div>
  );

  return (
    <motion.div key="share" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }} style={{ width: '100%', height: '100vh' }}>
      <CanvasView
        plan={plan}
        token={null}
        isOwner={false}
        shareToken={shareToken}
        shareRootId={shareRoot}
        initialBoxId={null}
        onBack={null}
        themePref={themePref}
        onThemeChange={onThemeChange}
      />
    </motion.div>
  );
}


// ── Public summary share page ──────────────────────────────────────────────────
function SummarySharePage() {
  const { token } = useParams();
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    fetch(`/api/summary-share/${token}`)
      .then(r => r.ok ? r.json() : Promise.reject('Invalid link'))
      .then(setData)
      .catch(() => setError('This summary link is invalid or has been removed.'));
  }, [token]);

  React.useEffect(() => {
    if (data?.title) document.title = `${data.title} — Summary`;
    return () => { document.title = 'IdeaCanvas'; };
  }, [data]);

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
      <div style={{ textAlign: 'center', color: '#888', fontSize: 14 }}>{error}</div>
    </div>
  );
  if (!data) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
      <div style={{ color: '#ccc', fontSize: 13, fontFamily: 'sans-serif' }}>Loading…</div>
    </div>
  );

  // Dynamically import ReactMarkdown for the public page
  return <SummaryShareView data={data} />;
}

function SummaryShareView({ data }) {
  const ReactMarkdown = require('react-markdown').default;
  const remarkGfm = require('remark-gfm').default;
  return (
    <div style={{ minHeight: '100vh', background: '#ffffff', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '60px 32px 80px' }}>
        {/* Header */}
        <div style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: 24, marginBottom: 40 }}>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: '#111', margin: '0 0 6px' }}>{data.title}</h1>
          <div style={{ fontSize: 13, color: '#999' }}>
            Executive Summary{data.author ? ` · ${data.author}` : ''}
          </div>
        </div>
        {/* Summary body */}
        <div style={{ fontSize: 16, lineHeight: 1.8, color: '#222' }}>
          {data.summary ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.summary}</ReactMarkdown>
          ) : (
            <p style={{ color: '#aaa' }}>No summary content yet.</p>
          )}
        </div>
        {/* Footer */}
        <div style={{ marginTop: 60, paddingTop: 20, borderTop: '1px solid #f0f0f0', fontSize: 12, color: '#ccc', textAlign: 'center' }}>
          Created with IdeaCanvas
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [auth, setAuth] = useState(getStoredAuth);

  function handleAuth(user, token) {
    setAuth({ user, token });
  }

  function handleLogout() {
    localStorage.removeItem('ic_token');
    localStorage.removeItem('ic_user');
    sessionStorage.removeItem('ic_token');
    sessionStorage.removeItem('ic_user');
    setAuth(null);
  }

  return (
    <BrowserRouter>
      <div style={{ width: '100vw', height: '100vh', overflow: 'hidden', background: 'var(--bg)' }}>
        <AppShell auth={auth} onAuth={handleAuth} onLogout={handleLogout} />
      </div>
    </BrowserRouter>
  );
}
